// src/components/training/CreateCourseModal.tsx
import React, { useState } from 'react';
import { X, BookOpen, Plus, Trash2, Video, FileText, HelpCircle, GripVertical } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { trainingService } from '../../services/training.service';
import { Course, CourseModule, ModuleType } from '../../types/training.types';
import { toast } from 'react-hot-toast';

interface CreateCourseModalProps {
  onClose: () => void;
  onCreated: (courseId: string) => void;
  organizationId: string;
}

const CreateCourseModal: React.FC<CreateCourseModalProps> = ({
  onClose,
  onCreated,
  organizationId
}) => {
  const { currentUser, userProfile } = useAuth();
  const [step, setStep] = useState<'info' | 'modules'>('info');
  const [saving, setSaving] = useState(false);

  // Course info
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState(30);

  // Modules
  const [modules, setModules] = useState<Partial<CourseModule>[]>([]);

  const addModule = (type: ModuleType) => {
    const newModule: Partial<CourseModule> = {
      id: `module_${Date.now()}`,
      title: '',
      description: '',
      type,
      order: modules.length,
      requiredForCompletion: true
    };
    setModules([...modules, newModule]);
  };

  const updateModule = (index: number, updates: Partial<CourseModule>) => {
    const updated = [...modules];
    updated[index] = { ...updated[index], ...updates };
    setModules(updated);
  };

  const removeModule = (index: number) => {
    setModules(modules.filter((_, i) => i !== index));
  };

  const moveModule = (fromIndex: number, toIndex: number) => {
    const updated = [...modules];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    // Update order values
    updated.forEach((m, i) => {
      m.order = i;
    });
    setModules(updated);
  };

  const handleSave = async (publish: boolean) => {
    if (!title.trim()) {
      toast.error('Please enter a course title');
      return;
    }

    if (modules.length === 0) {
      toast.error('Please add at least one module');
      return;
    }

    for (const mod of modules) {
      if (!mod.title?.trim()) {
        toast.error('All modules must have a title');
        return;
      }
    }

    setSaving(true);
    try {
      const courseData: Omit<Course, 'id' | 'createdAt' | 'updatedAt' | 'enrollmentCount' | 'completionCount'> = {
        organizationId,
        title: title.trim(),
        description: description.trim(),
        status: publish ? 'published' : 'draft',
        modules: modules.map((m, i) => ({
          id: m.id || `module_${i}`,
          title: m.title || '',
          description: m.description || '',
          type: m.type || 'text',
          order: i,
          duration: m.duration,
          videoUrl: m.videoUrl,
          documentUrl: m.documentUrl,
          documentName: m.documentName,
          textContent: m.textContent,
          quiz: m.quiz,
          requiredForCompletion: m.requiredForCompletion ?? true
        })),
        category: category || undefined,
        estimatedDuration,
        createdBy: currentUser?.uid || '',
        createdByName: userProfile?.name || userProfile?.displayName || 'Admin'
      };

      const courseId = await trainingService.createCourse(courseData);

      if (publish) {
        await trainingService.publishCourse(courseId);
      }

      toast.success(publish ? 'Course published successfully!' : 'Course saved as draft');
      onCreated(courseId);
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error('Failed to create course');
    } finally {
      setSaving(false);
    }
  };

  const getModuleIcon = (type: ModuleType) => {
    switch (type) {
      case 'video': return <Video className="h-5 w-5" />;
      case 'document': return <FileText className="h-5 w-5" />;
      case 'quiz': return <HelpCircle className="h-5 w-5" />;
      default: return <BookOpen className="h-5 w-5" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Create New Course</h2>
            <p className="text-sm text-gray-500 mt-1">
              {step === 'info' ? 'Step 1: Course Details' : 'Step 2: Add Modules'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'info' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Field Safety Training"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what users will learn in this course..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    <option value="Onboarding">Onboarding</option>
                    <option value="Safety">Safety</option>
                    <option value="Compliance">Compliance</option>
                    <option value="Skills">Skills</option>
                    <option value="Product">Product Knowledge</option>
                    <option value="Policy">Policy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(parseInt(e.target.value) || 0)}
                    min={1}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 'modules' && (
            <div className="space-y-6">
              {/* Add Module Buttons */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Add Module</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { type: 'text' as ModuleType, label: 'Text', icon: BookOpen },
                    { type: 'video' as ModuleType, label: 'Video', icon: Video },
                    { type: 'document' as ModuleType, label: 'Document', icon: FileText },
                    { type: 'quiz' as ModuleType, label: 'Quiz', icon: HelpCircle }
                  ].map(({ type, label, icon: Icon }) => (
                    <button
                      key={type}
                      onClick={() => addModule(type)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Module List */}
              {modules.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                  <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No modules added yet</p>
                  <p className="text-sm text-gray-400">Click the buttons above to add course content</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {modules.map((module, index) => (
                    <div
                      key={module.id}
                      className="border border-gray-200 rounded-xl p-4 bg-white"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-5 w-5 text-gray-400 cursor-grab" />
                          <div className="p-2 bg-primary-100 rounded-lg">
                            {getModuleIcon(module.type || 'text')}
                          </div>
                        </div>

                        <div className="flex-1 space-y-3">
                          <input
                            type="text"
                            value={module.title || ''}
                            onChange={(e) => updateModule(index, { title: e.target.value })}
                            placeholder="Module title"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />

                          <textarea
                            value={module.description || ''}
                            onChange={(e) => updateModule(index, { description: e.target.value })}
                            placeholder="Module description (optional)"
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                          />

                          {/* Type-specific fields */}
                          {module.type === 'video' && (
                            <input
                              type="url"
                              value={module.videoUrl || ''}
                              onChange={(e) => updateModule(index, { videoUrl: e.target.value })}
                              placeholder="Video URL (YouTube, Vimeo, etc.)"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                            />
                          )}

                          {module.type === 'document' && (
                            <input
                              type="url"
                              value={module.documentUrl || ''}
                              onChange={(e) => updateModule(index, { documentUrl: e.target.value })}
                              placeholder="Document URL or upload link"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                            />
                          )}

                          {module.type === 'text' && (
                            <textarea
                              value={module.textContent || ''}
                              onChange={(e) => updateModule(index, { textContent: e.target.value })}
                              placeholder="Enter text content here..."
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                            />
                          )}

                          {module.type === 'quiz' && (
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-sm text-gray-600">
                                Quiz creation is available in the course editor after saving.
                              </p>
                            </div>
                          )}

                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={module.requiredForCompletion ?? true}
                                onChange={(e) => updateModule(index, { requiredForCompletion: e.target.checked })}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              Required for completion
                            </label>

                            <input
                              type="number"
                              value={module.duration || ''}
                              onChange={(e) => updateModule(index, { duration: parseInt(e.target.value) || undefined })}
                              placeholder="Duration (min)"
                              min={1}
                              className="w-32 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                            />
                          </div>
                        </div>

                        <button
                          onClick={() => removeModule(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex items-center justify-between">
          {step === 'modules' ? (
            <button
              onClick={() => setStep('info')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>

            {step === 'info' ? (
              <button
                onClick={() => {
                  if (!title.trim()) {
                    toast.error('Please enter a course title');
                    return;
                  }
                  setStep('modules');
                }}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Next: Add Modules
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Save as Draft
                </button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  )}
                  Publish Course
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCourseModal;
